import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Phone } from 'lucide-react';
import { SITE } from '../../data/site';
import { SectionHead, Reveal } from '../../components/Bits';
import { Img } from '../../components/Img';

const PAGE_URL = 'https://biryanify.us/blog/best-indian-restaurant-irving-tx';
const TITLE = 'Best Indian Restaurant in Irving, TX | Biryanify — Biryani, Mandi & Kebabs';
const DESCRIPTION =
  'Searching for the best restaurant in Irving, TX? Here\u2019s why Biryanify — near Las Colinas & Irving Mall — is the top pick for Hyderabadi biryani, Arabian mandi and Indian food, open now for pickup.';

const FAQS = [
  {
    q: 'What is the best Indian restaurant in Irving, TX?',
    a: 'Biryanify, located at 9400 N MacArthur Blvd in Irving (near Las Colinas and Valley Ranch), is a top-rated choice for Hyderabadi dum biryani, Arabian mandi, South Indian tiffins and kebabs. The menu covers everything from chicken 65 to masala dosa, all cooked fresh daily.',
  },
  {
    q: 'Is there a good restaurant near Irving Mall that serves biryani?',
    a: 'Yes. Biryanify serves authentic dum-cooked biryani and mandi a short drive from Irving Mall and the Las Colinas business district, making it an easy stop for lunch, dinner, or takeout on your way home.',
  },
  {
    q: 'Does Biryanify take orders for pickup right now?',
    a: 'Biryanify is open daily from 11:00 AM \u2014 10:00 PM (Sunday\u2013Thursday) and until 11:00 PM on Friday and Saturday. You can order online for pickup any time the restaurant is open, so if you\u2019re searching "restaurant near me open now," Biryanify is ready to take your order.',
  },
  {
    q: 'What makes Hyderabadi biryani different from other biryani styles?',
    a: 'Hyderabadi biryani is cooked using the "dum" method \u2014 marinated meat and basmati rice are layered raw, sealed under a tight lid, and slow-steamed together so the rice absorbs the meat\u2019s juices and whole spices like saffron, mint and fried onion. It\u2019s a slower, more labor-intensive process than steam-table biryani, which is why the flavor runs so much deeper.',
  },
  {
    q: 'Does Biryanify have vegetarian options?',
    a: 'Yes \u2014 Biryanify keeps a full vegetarian menu alongside its non-veg dishes, including veg biryani, dosas, idli, and South Indian curries, so mixed groups can order together easily.',
  },
];

function useSeoHead() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;

    const setMeta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', DESCRIPTION);
    setMeta('og:title', TITLE, 'property');
    setMeta('og:description', DESCRIPTION, 'property');
    setMeta('og:url', PAGE_URL, 'property');
    setMeta('og:type', 'article', 'property');
    setMeta('twitter:title', TITLE);
    setMeta('twitter:description', DESCRIPTION);

    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const prevCanonical = canonical?.getAttribute('href') || '';
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', PAGE_URL);

    const ldScript = document.createElement('script');
    ldScript.type = 'application/ld+json';
    ldScript.id = 'blog-jsonld';
    ldScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://biryaniify.us/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Best Indian Restaurant in Irving, TX',
              item: PAGE_URL,
            },
          ],
        },
        {
          '@type': 'Article',
          headline: 'Best Indian Restaurant in Irving, TX: Your Guide to Biryani, Mandi & More',
          description: DESCRIPTION,
          author: { '@type': 'Organization', name: 'Biryanify' },
          publisher: { '@type': 'Organization', name: 'Biryanify' },
          mainEntityOfPage: PAGE_URL,
          url: PAGE_URL,
          datePublished: '2024-12-18',
          dateModified: '2024-12-18',
        },
        {
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(ldScript);

    return () => {
      document.title = prevTitle;
      if (canonical) canonical.setAttribute('href', prevCanonical || 'https://biryanify.us/');
      document.getElementById('blog-jsonld')?.remove();
    };
  }, []);
}

export default function BestIndianRestaurantIrvingTx() {
  useSeoHead();

  return (
    <div style={{ background: 'var(--color-surface)' }}>
      <section className="relative min-h-[46vh] flex flex-col justify-end overflow-hidden">
        <Img
          src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-07-03/7db311fc9283.png"
          alt="Overhead shot of Hyderabadi chicken biryani served at Biryanify, an Indian restaurant in Irving, TX"
          eager
          className="absolute inset-0"
          imgClassName="scale-105"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.6) 0%, rgba(20,13,8,0.25) 45%, rgba(20,13,8,0.92) 100%)' }} />
        <div className="wrap relative pb-12 pt-32 md:pb-16">
          <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Irving, TX Dining Guide</p>
          <h1 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2.2rem, 5.6vw, 3.6rem)', lineHeight: 1.05, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}>
            Best Indian Restaurant in Irving, TX: A Local Guide to Biryani &amp; Mandi
          </h1>
          <p className="mt-4 max-w-2xl" style={{ fontSize: '1.08rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
            Looking for the best restaurant in Irving, TX near Las Colinas or Irving Mall? Here&rsquo;s what to know
            before you search &ldquo;restaurant near me open now&rdquo; tonight.
          </p>
        </div>
      </section>

      <article className="wrap py-14 md:py-20 max-w-3xl mx-auto">
        <Reveal>
          <p className="text-ink-2" style={{ fontSize: '1.08rem', lineHeight: 1.75 }}>
            If you&rsquo;ve typed &ldquo;restaurant in Irving TX&rdquo;, &ldquo;best restaurant Irving TX&rdquo;, or
            &ldquo;best restaurants Irving TX Indian&rdquo; into Google tonight, you&rsquo;re in the right place. Irving —
            and the Las Colinas business corridor in particular — has no shortage of dining options, but genuine,
            slow-cooked Hyderabadi biryani is harder to find than you&rsquo;d think. This guide breaks down what makes
            a great Indian restaurant in Irving, and why <Link to="/" className="underline font-semibold">Biryanify</Link>{' '}
            at 9400 N MacArthur Blvd has become a go-to spot for biryani, mandi and South Indian tiffins near Irving Mall
            and Valley Ranch.
          </p>
        </Reveal>

        <Reveal delay={80}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">What to Look for in the Best Restaurant in Irving, TX</h2>
          <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
            Whether you&rsquo;re comparing options in Las Colinas, near Irving Mall, or across the DFW metro, a handful
            of things separate an average meal from a memorable one:
          </p>
          <ul className="mt-5 space-y-3 text-ink-2" style={{ lineHeight: 1.7 }}>
            <li><strong className="text-ink">Food cooked to order, not steamed on a buffet line.</strong> Real Hyderabadi biryani is sealed and cooked &ldquo;on dum&rdquo; — a slow-steam method that takes time but delivers far more flavor than a warming tray ever could.</li>
            <li><strong className="text-ink">A menu that covers more than one region.</strong> The best restaurants blend Hyderabadi biryani, South Indian tiffins (dosa, idli, uthappam), Arabian mandi, and North Indian-style kebabs so every craving is covered in one visit.</li>
            <li><strong className="text-ink">Reliable hours and easy online ordering</strong> for the &ldquo;restaurant near me open now&rdquo; moments — after work in Las Colinas, after a trip to Irving Mall, or a late Friday night.</li>
            <li><strong className="text-ink">Fresh ingredients daily</strong>, not dishes held over from the day before.</li>
          </ul>
        </Reveal>

        <Reveal delay={100}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Why Biryanify Stands Out Near Las Colinas &amp; Irving Mall</h2>
          <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
            <Link to="/about" className="underline font-semibold">Biryanify</Link> was built around one idea: biryani
            isn&rsquo;t fast food. Basmati rice is layered with marinated chicken, goat, or vegetables and whole spices,
            sealed under a tight lid, and finished slowly over dum until every grain carries the aroma of saffron, mint
            and fried onion. Around that centerpiece sits a full South Indian and Arabian spread:
          </p>
          <ul className="mt-5 space-y-3 text-ink-2" style={{ lineHeight: 1.7 }}>
            <li>Tawa-crisp <strong className="text-ink">dosa and idli</strong> for a lighter, South Indian-style meal</li>
            <li>Fiery <strong className="text-ink">chicken 65</strong> and kebabs to start</li>
            <li>Smoke-kissed <strong className="text-ink">Al Faham mandi</strong>, Arabian-style grilled chicken over saffron rice</li>
            <li>Cooling <strong className="text-ink">falooda and kulfi</strong> to finish the meal</li>
          </ul>
          <p className="mt-4 text-ink-2" style={{ lineHeight: 1.75 }}>
            See the full spread on the <Link to="/menu" className="underline font-semibold">menu page</Link>, including
            prices and dietary tags for vegetarian, egg, and non-veg dishes.
          </p>
        </Reveal>

        <Reveal delay={120}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Restaurant Near Me, Open Now: Hours &amp; Location</h2>
          <div className="mt-5 card p-6 md:p-7 grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <MapPin size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">{SITE.address.line1}</div>
                <div className="text-ink-2">{SITE.address.line2}</div>
                <div className="text-ink-2 text-sm mt-1">Minutes from Irving Mall &amp; Las Colinas</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-ink">Open daily</div>
                <div className="text-ink-2">Sun–Thu · 11 AM – 10 PM</div>
                <div className="text-ink-2">Fri–Sat · 11 AM – 11 PM</div>
              </div>
            </div>
            <div className="flex items-start gap-3 sm:col-span-2">
              <Phone size={20} style={{ color: 'var(--color-accent)' }} className="mt-0.5 shrink-0" />
              <a href={SITE.phoneHref} className="font-semibold text-ink hover:underline">{SITE.phone}</a>
            </div>
          </div>
          <p className="mt-5 text-ink-2" style={{ lineHeight: 1.75 }}>
            Check full details and get directions on the <Link to="/visit" className="underline font-semibold">Visit &amp; Hours page</Link>,
            or head straight to the <Link to="/menu" className="underline font-semibold">menu</Link> to start an order for pickup.
          </p>
        </Reveal>

        <Reveal delay={140}>
          <h2 className="font-display mt-12 text-2xl md:text-3xl text-ink">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-6">
            {FAQS.map((f) => (
              <div key={f.q} className="card p-6">
                <h3 className="font-display text-lg text-ink">{f.q}</h3>
                <p className="mt-2 text-ink-2" style={{ lineHeight: 1.7 }}>{f.a}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={160}>
          <div className="mt-14 card p-8 text-center" style={{ background: 'var(--color-surface-2)' }}>
            <h2 className="font-display text-2xl text-ink">Ready to taste it for yourself?</h2>
            <p className="mt-3 text-ink-2">Browse the menu and order online for pickup at our Irving kitchen.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/menu" className="btn btn-primary">View the menu <ArrowRight size={16} /></Link>
              <Link to="/visit" className="btn btn-ghost">Get directions</Link>
            </div>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
